import { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react';
import { useGlobal } from 'hooks/useGlobal';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';
import BootstrapForm from 'react-bootstrap/Form';
import { SeriesOptionsType } from 'highcharts';
import { DropdownIndicatorProps, OptionProps } from 'react-select';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { UrlParams } from 'common/types';
import { DashboardV2 } from 'common/graphqlTypes';
import { buildUrl } from 'common/util/location';
import { extractFacetExpression } from 'common/util/graph';

import Button, { ButtonVariant } from 'components/common/Button/Button';
import CheckboxRadio from 'components/common/CheckboxRadio/CheckboxRadio';
import Dialog from 'components/common/Dialog/Dialog';
import Toolbar from 'components/common/Toolbar/Toolbar';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import Select, { components, OptionType } from 'components/common/ReactSelect/Select';
import {
  completeFilterState,
  routeState,
  searchParamRecordState,
} from 'components/common/Managers/RouteStateManager.state';
import { DashboardJSONType } from 'components/dashboards/types';
import { ADD_GRAPH_TO_DASHBOARD, GET_CONFIGV2, LIST_DASHBOARD_NAMES } from 'components/dashboards/queries';
import { dashboardListState } from 'components/account/account.state';

import './SaveToDashboard.scss';

type SaveToDashboardProps = {
  show: boolean;
  close: () => void;
};

type Override<T1, T2> = Omit<T1, keyof T2> & T2;

/**
 * Build serialized graph configuration JSON based on the current search query.
 *
 * @param title              Title of the graph to add to the dashboard
 * @param route              The current location of the user
 * @param searchParams       State object representing the current params in the URL
 * @param completeFilter     Complete filter including any filter state from url params or the current view
 * @param plots{optional}    An array of plot objects
 */
const translateForDashboard = (
  title: string,
  route: string,
  searchParams: UrlParams,
  completeFilter: string,
  plots: Array<SeriesOptionsType>,
): string => {
  const addIfDefined = (addTo: DashboardJSONType, name: string, aka?: string, isNumeric = false): void => {
    if (name in searchParams && Object(searchParams)[name]) {
      addTo[aka || name] = isNumeric ? Number(Object(searchParams)[name]) : Object(searchParams)[name];
    }
  };

  const json: DashboardJSONType = {};
  json.title = title;
  const facet = searchParams[K.URL_PARAM.FACET];
  const isRate = facet === K.RATE.op || facet === '';
  const teamEmails = searchParams[K.URL_PARAM.TEAM_EMAILS]
    ? searchParams[K.URL_PARAM.TEAM_EMAILS].split(',')
    : undefined;
  json.teamEmails = teamEmails;

  if (route === K.ROUTE.POWER_QUERY) {
    json.query = completeFilter;
    // if pieStyle url param has a value, we use that as the graphStyle (Donut/Pie), otherwise, use standard graphStyle param.
    const graphStyle = searchParams[K.URL_PARAM.PIE_STYLE] ? K.URL_PARAM.PIE_STYLE : K.URL_PARAM.GRAPH_STYLE;
    addIfDefined(json, graphStyle, K.URL_PARAM.GRAPH_STYLE);
    addIfDefined(json, K.URL_PARAM.MAX_PIE_SLICES, undefined, true);
    addIfDefined(json, K.URL_PARAM.LINE_SMOOTHING);
    addIfDefined(json, K.URL_PARAM.Y_SCALE);
  } else if (route === K.ROUTE.GRAPH) {
    json.graphStyle = searchParams[K.URL_PARAM.GRAPH_STYLE] || K.CHART_TYPES.LINE;
    addIfDefined(json, K.URL_PARAM.LINE_SMOOTHING);
    addIfDefined(json, K.URL_PARAM.BAR_WIDTH, json.graphStyle === K.CHART_TYPES.LINE ? 'barWidth' : undefined);
    addIfDefined(json, K.URL_PARAM.BREAKDOWN_FACET);
    addIfDefined(json, K.URL_PARAM.Y_SCALE);
    addIfDefined(json, K.URL_PARAM.NUM_BARS);

    // if standard rate graph
    if (isRate && !searchParams.breakdownFacet && !searchParams.compareTimeOffset) {
      const plot: DashboardJSONType = {
        filter: completeFilter,
        label: 'Rate',
      };
      json.plots = [plot];

      // if breakdown graph
    } else if (searchParams.breakdownFacet) {
      const plot: DashboardJSONType = {
        filter: completeFilter,
        label: searchParams.facet || 'Rate',
      };

      addIfDefined(plot, K.URL_PARAM.FACET);
      json.plots = [plot];

      // if comparison graph
    } else if (searchParams.compareTimeOffset) {
      const graphs = new Set(['main', 'shifted']);
      (searchParams[K.URL_PARAM.HIDE_GRAPHS] ? JSON.parse(searchParams[K.URL_PARAM.HIDE_GRAPHS]) : []).forEach(
        (key: string) => graphs.delete(key),
      );
      (searchParams[K.URL_PARAM.SHOW_GRAPHS] ? JSON.parse(searchParams[K.URL_PARAM.SHOW_GRAPHS]) : []).forEach(
        (key: string) => graphs.add(key),
      );
      const func = isRate ? 'countPerSec' : 'mean';
      const graphPlots: DashboardJSONType[] = [];

      if (graphs.has('main')) {
        graphPlots.push({
          filter: `${func}(${isRate ? 'value' : facet} where ${completeFilter || "''"})`,
          label: facet,
        });
      }

      if (graphs.has('shifted')) {
        graphPlots.push({
          filter: `${func}(${isRate ? 'value' : facet} timeshift ${searchParams.compareTimeOffset} where ${
            completeFilter || "''"
          })`,
          label: `shifted(${facet})`,
        });
      }

      if (graphs.has('delta')) {
        graphPlots.push({
          filter: `${func}(${isRate ? 'value' : facet} where ${completeFilter || "''"}) - ${func}(${
            isRate ? 'value' : facet
          } timeshift ${searchParams.compareTimeOffset} where ${completeFilter || "''"})`,
          label: `delta(${facet})`,
        });
      }

      json.plots = graphPlots;

      // if facet value graph
    } else if (searchParams.facet) {
      json.plots = plots.map((seriesPlot) => {
        const plot: DashboardJSONType = {
          filter: completeFilter,
          label: searchParams.facet,
          facet: searchParams.facet,
        };

        if (seriesPlot?.custom?.op) {
          const extractedFacet = extractFacetExpression(searchParams.facet);
          plot.facet = `${seriesPlot.custom.op}(${extractedFacet.facetVariable})`;
          plot.label = plot.facet;
        }

        if (seriesPlot?.color) {
          plot.color = seriesPlot.color;
        }

        return plot;
      });
    }
    // if distribution graph
  } else if (route === K.ROUTE.DISTRIBUTION) {
    json.graphStyle = 'distribution';
    json.label = searchParams.facet;
    json.facet = searchParams.facet;
    if (searchParams.filter) {
      json.filter = completeFilter;
    }
  } else {
    json.graphStyle = searchParams[K.URL_PARAM.GRAPH_STYLE] || 'line';
    addIfDefined(json, K.URL_PARAM.LINE_SMOOTHING);
    addIfDefined(json, K.URL_PARAM.BAR_WIDTH, json.graphStyle === 'line' ? 'smoothingInterval' : undefined);

    const plot: DashboardJSONType = { filter: completeFilter };
    addIfDefined(plot, K.URL_PARAM.FACET);
    addIfDefined(plot, K.URL_PARAM.BREAKDOWN_FACET);
    json.plots = [plot];
  }
  return JSON.stringify(json);
};

/**
 * This component allows the user to save a search/graph/pquery to a new or existing dashboard.
 *
 * @prop title        the title of the dialog
 * @prop show         if true, the dialog is displayed
 * @prop close        function to call to close the dialog
 *
 */
function SaveToDashboard({ show, close }: SaveToDashboardProps): JSX.Element {
  const route = useRecoilValue(routeState);
  const searchParams = useRecoilValue(searchParamRecordState);
  const completeFilter = useRecoilValue(completeFilterState);
  const [graphTitle, setGraphTitle] = useState('');
  const [isAddNewDashboard, setIsAddNewDashboard] = useState('false');
  const [selectedDashboard, setSelectedDashboard] = useState('');
  const [dashboardName, setDashboardName] = useState('');
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const dashboards = useRecoilValue(dashboardListState);
  const [seriesData] = useGlobal(K.GLOBAL_KEY.GRAPH_SERIES_DATA);
  const [tabName, setTabName] = useState('');
  const getDashboardName = (): string => (isAddNewDashboard === 'true' ? dashboardName : selectedDashboard);

  const itemName = route === K.ROUTE.POWER_QUERY ? 'table' : 'graph';
  const [validated, setValidated] = useState(false);
  const [addGraphToDashboard] = useMutation(ADD_GRAPH_TO_DASHBOARD, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: getDashboardName() } }, { query: LIST_DASHBOARD_NAMES }],
    onCompleted: () => {
      close();
      setIsAddNewDashboard('false');
      setPageAlertConfig({
        message: `Saved to ${getDashboardName()} |`,
        variant: PageAlertVariant.INFO,
        linkHref: buildUrl(K.ROUTE.DASHBOARD, { [K.URL_PARAM.DASHBOARD_NAME]: getDashboardName() } as UrlParams),
        linkText: 'Go to dashboard',
        autoHide: true,
        duration: 5,
      });
    },
  });

  const plots = seriesData.filter((plot) => plot.visible) as Array<SeriesOptionsType>;

  // Initialize the dashboard dropdown to select the first dashboard by default.
  useEffect(() => {
    if (dashboards) {
      setSelectedDashboard(dashboards?.[0]?.name || '');
      setTabName(dashboards?.[0]?.tabs[0]?.tabName || '');
    }
  }, [dashboards]);

  const handleInputChange =
    (setter: (value: string) => void) =>
    (ev: SyntheticEvent): void => {
      const inputElement = ev.target as HTMLInputElement;
      setter(inputElement.value);
    };

  const handleDashboardNameChange = (ev: SyntheticEvent): void => {
    const inputElement = ev.target as HTMLInputElement;
    setDashboardName(inputElement.value);
    setValidated(true);
    if (inputElement.validity.patternMismatch) {
      inputElement.setCustomValidity(
        'A dashboard name can only contain letters, digits, spaces, "/", "_", "-", or "."',
      );
    } else {
      inputElement.setCustomValidity('');
    }
  };

  const handleSubmit = (event: SyntheticEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    setValidated(true);
    const form = event.currentTarget as HTMLFormElement;
    if (form.checkValidity()) {
      addGraphToDashboard({
        variables: {
          dashboardName: getDashboardName(),
          graph: translateForDashboard(graphTitle, route, searchParams, completeFilter, plots),
          tabName,
        },
      });
    }
  };

  const dashboardSections = dashboards.map((dashboard: DashboardV2) => ({
    value: dashboard.name,
    label: dashboard.name,
    tabName: dashboard.tabs[0]?.tabName,
  }));

  const existingDashboardsSelectComponent = (
    <Select
      options={dashboardSections}
      isDisabled={dashboardSections.length === 0}
      defaultValue={{ value: selectedDashboard, label: selectedDashboard }}
      onChange={(selectedOption) => {
        const { value, tabName } = selectedOption as OptionType;
        setSelectedDashboard(value as string);
        setTabName(tabName);
      }}
      components={{
        // @ts-expect-error
        DropdownIndicator: (({ innerProps, ...props }: Override<DropdownIndicatorProps<{}>, { innerProps: any }>) => {
          innerProps = {
            ...innerProps,
            'data-test-id': ELEMENT.saveToDashboardDialogExistingDashboardDropdownIndicator,
          };
          return <components.DropdownIndicator {...props} innerProps={innerProps} />;
        }) as FunctionComponent<DropdownIndicatorProps<{}>>,
        // @ts-expect-error
        Option: (({ innerProps, ...props }: Override<OptionProps<{}>, { innerProps: any }>): unknown => {
          innerProps = {
            ...innerProps,
            'data-test-id': ELEMENT.saveToDashboardDialogExistingDashboardName,
          };
          return <components.Option {...props} innerProps={innerProps} />;
        }) as FunctionComponent<OptionProps<{}>>,
      }}
    />
  );

  const newDashboardInputComponent = (
    <>
      <BootstrapForm.Control
        onClick={(): void => setIsAddNewDashboard('true')}
        as="input"
        required
        pattern="^[\w\s._/-]+$"
        className="SaveToDashboard__SubInputElement"
        value={dashboardName}
        onChange={handleDashboardNameChange}
        data-test-id={ELEMENT.saveToDashboardDialogNewDashboardName}
      />
      <BootstrapForm.Control.Feedback type="invalid">
        Please enter a valid name for the new dashboard.
      </BootstrapForm.Control.Feedback>
    </>
  );

  // Before cancelling, reset the graph title in case the user brings up the dialog again
  // But leave the others intact so the user can add more to the same dashboard
  const closeDialog = (): void => {
    setValidated(false);
    setGraphTitle('');
    setIsAddNewDashboard('false');
    close();
  };

  // We use custom buttons as we need the OK button to be of type "submit"
  const buttons = [
    <Button variant={ButtonVariant.PRIMARY} type="submit" testId={ELEMENT.saveToDashboardDialogOk}>
      OK
    </Button>,
    <Button variant={ButtonVariant.SECONDARY} onClick={closeDialog} testId={ELEMENT.saveToDashboardDialogCancel}>
      Cancel
    </Button>,
  ];

  const itemCap = itemName.charAt(0).toUpperCase() + itemName.substring(1);
  const titleLabel = `${itemCap} Title`;
  const emptyNewDashboardError = `Please enter a name for this new dashboard ${itemName}.`;
  return (
    <Dialog
      title="Save to Dashboard"
      show={show}
      close={close}
      footer={{ omit: true }}
      testId={ELEMENT.saveToDashboardDialog}
      scrollable={false}
    >
      <BootstrapForm noValidate validated={validated} className="SaveToDashboard__Form" onSubmit={handleSubmit}>
        <BootstrapForm.Group>
          <BootstrapForm.Label className="SaveToDashboard__TitlePrompt">{titleLabel}</BootstrapForm.Label>
          <BootstrapForm.Control
            as="input"
            required
            value={graphTitle}
            placeholder={`What to call this ${itemName}`}
            onChange={handleInputChange(setGraphTitle)}
            data-test-id={ELEMENT.saveToDashboardDialogGraphTitle}
          />
          <BootstrapForm.Control.Feedback type="invalid">{emptyNewDashboardError}</BootstrapForm.Control.Feedback>
        </BootstrapForm.Group>
        <BootstrapForm.Group>
          <BootstrapForm.Label className="SaveToDashboard__DashboardPrompt">Use</BootstrapForm.Label>
          <CheckboxRadio
            type="radio"
            label={
              <BootstrapForm.Label
                className="SaveToDashboard__RadioLabel"
                htmlFor="SaveToDashboard__RadioButton--ExistingDashboard"
              >
                Existing Dashboard
              </BootstrapForm.Label>
            }
            id="SaveToDashboard__RadioButton--ExistingDashboard"
            className="SaveToDashboard__RadioButton"
            value="false"
            checked={isAddNewDashboard === 'false'}
            onChange={handleInputChange(setIsAddNewDashboard)}
            testId={ELEMENT.saveToDashboardDialogExistingDashboard}
          />
          <CheckboxRadio
            type="radio"
            label={
              <BootstrapForm.Label
                className="SaveToDashboard__RadioLabel"
                htmlFor="SaveToDashboard__RadioButton--NewDashboard"
              >
                New Dashboard
              </BootstrapForm.Label>
            }
            id="SaveToDashboard__RadioButton--NewDashboard"
            className="SaveToDashboard__RadioButton"
            value="true"
            checked={isAddNewDashboard === 'true'}
            onChange={handleInputChange(setIsAddNewDashboard)}
            testId={ELEMENT.saveToDashboardDialogNewDashboard}
          />
          {isAddNewDashboard === 'true' ? newDashboardInputComponent : existingDashboardsSelectComponent}
        </BootstrapForm.Group>
        <Toolbar rightItems={buttons} />
      </BootstrapForm>
    </Dialog>
  );
}

export default SaveToDashboard;
